"use client";

import React from "react";
import { useRouter } from 'next/navigation';
import {
  Button,
  HeroWithImages,
  UniversityCarousel,
  RedefiningStandards,
  HowItWorksSimple,
  TestimonialsSection,
  CommunityPaymentModel,
  TherapistTiersClear
} from "@repo/ui";

export default function HomePage() {
  const router = useRouter();
  
  return (
    <div className="min-h-screen bg-white">
      <HeroWithImages
        title="Find your perfect therapist match"
        subtitle="Quality therapy that fits your budget. Starting at $45."
        ctaText="Get matched now"
        trustedByCount="38,000+ Canadians"
        onCtaClick={() => router.push('/quiz')}
      />

      {/* Free First Session Banner */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                  <span className="text-xl">🎉</span>
                </div>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold text-white">
                  Your first session is FREE with intern therapists
                </p>
                <p className="text-xs sm:text-sm text-white/90">
                  No credit card required • Master's-level therapists • Professionally supervised
                </p>
              </div>
            </div>
            <button 
              className="px-4 py-2 bg-white text-indigo-600 hover:bg-indigo-50 font-semibold rounded-lg shadow-lg whitespace-nowrap border border-white/20 transition-colors text-sm"
              onClick={() => router.push('/quiz')}
            >
              Get Matched →
            </button>
          </div>
        </div>
      </div>

      <UniversityCarousel />

      <RedefiningStandards onMeetTherapistsClick={() => router.push('/therapists')} />

      {/* Simple How It Works */}
      <div id="how-it-works" className="scroll-mt-20">
        <HowItWorksSimple onGetStarted={() => router.push('/quiz')} />
      </div>

      {/* Testimonials Section */}
      <TestimonialsSection
        title="What our clients are saying"
        rating={{
          score: "4.6",
          // platform: "Trustpilot" // Temporarily commented out
        }}
        description="Join thousands of Canadians who have found affordable, quality therapy through Alli"
        testimonials={[
          <div key="1" className="bg-white rounded-xl p-6 shadow-md min-w-[320px] max-w-[320px]">
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "Best Decision Ever! The price is accessible, processes are seamless. This service has been incredibly beneficial for my mental health journey."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-semibold">
                C
              </div>
              <div>
                <p className="font-medium text-gray-900">Customer</p>
                <p className="text-sm text-gray-600">Verified Review</p>
              </div>
            </div>
          </div>,
          <div key="2" className="bg-white rounded-xl p-6 shadow-md min-w-[320px] max-w-[320px]">
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "After leaving university I felt lost. I was unable to afford therapy due to the high cost of most therapists. Alli made it possible for me to get the help I needed."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold">
                A
              </div>
              <div>
                <p className="font-medium text-gray-900">Amberley</p>
                <p className="text-sm text-gray-600">Verified Review</p>
              </div>
            </div>
          </div>,
          <div key="3" className="bg-white rounded-xl p-6 shadow-md min-w-[320px] max-w-[320px]">
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "This is a definite source of affordable therapy - I highly recommend using them. They've provided me with helpful tools to navigate my challenges."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-semibold">
                M
              </div>
              <div>
                <p className="font-medium text-gray-900">Marie Nicole</p>
                <p className="text-sm text-gray-600">Verified Review</p>
              </div>
            </div>
          </div>,
          <div key="4" className="bg-white rounded-xl p-6 shadow-md min-w-[320px] max-w-[320px]">
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "I like everything about this service. I love my therapist, setting up appointments is very simple. The whole experience has been wonderful."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white font-semibold">
                K
              </div>
              <div>
                <p className="font-medium text-gray-900">Kristina S.</p>
                <p className="text-sm text-gray-600">Verified Review</p>
              </div>
            </div>
          </div>,
          <div key="5" className="bg-white rounded-xl p-6 shadow-md min-w-[320px] max-w-[320px]">
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-4">
              "Good therapists, affordable price. Exactly what I was looking for - quality mental health support that doesn't break the bank."
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                E
              </div>
              <div>
                <p className="font-medium text-gray-900">Evan</p>
                <p className="text-sm text-gray-600">Verified Review</p>
              </div>
            </div>
          </div>
        ]}
        // footerLink={{
        //   text: "Read more reviews",
        //   url: "https://www.trustpilot.com/review/alli.io",
        //   platform: "Trustpilot"
        // }}
      />

      <div id="pricing" className="scroll-mt-20">
        <CommunityPaymentModel />
      </div>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-primary-50 to-white rounded-2xl p-8 text-center border border-primary-100">
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
              Join 38,000+ Canadians in getting the support you deserve
            </h3>
            <p className="text-gray-600 mb-6">
              2-minute quiz to find your perfect therapist match
            </p>
            <Button 
              size="lg"
              className="bg-primary-600 hover:bg-primary-700 shadow-lg hover:shadow-xl transition-shadow"
              onClick={() => router.push('/quiz')}
            >
              Take the Matching Quiz →
            </Button>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>2 minutes</span>
              </div>
              <span className="hidden sm:inline text-gray-400">•</span>
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>100% confidential</span>
              </div>
              <span className="hidden sm:inline text-gray-400">•</span>
              <div className="flex items-center gap-1">
                <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>No commitment</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Therapist Tiers - Clear Version */}
      <TherapistTiersClear 
        onCtaClick={() => router.push('/quiz')}
        onMeetTherapistsClick={() => router.push('/therapists')}
      />
    </div>
  );
}